import { action, computed, makeObservable, observable } from 'mobx';

import { ENDPOINTS } from '@/configs/endpoints';
import { notificationsStore } from '@/stores/global/RootStore';
import { ILocalStore } from '@/stores/interfaces/ILocalStore';
import { MetaStore } from '@/stores/local/shared/MetaStore';
import { OpenStore } from '@/stores/local/shared/OpenStore';
import { FieldTextStore } from '@/stores/local/shared/form/FieldTextStore';
import call from '@/utils/api';

type AnswerRatingStoreParams = {
  organizationId: number;
  sessionId: () => string | null;
};

export type Rating = 'positive' | 'negative';

export class AnswerRatingStore implements ILocalStore {
  readonly meta = new MetaStore();
  readonly feedbackMeta = new MetaStore();

  readonly successFeedbackModal = new OpenStore();
  readonly feedbackModal = new OpenStore();

  readonly fieldText = new FieldTextStore();

  private readonly _organizationId: string;
  private readonly _sessionId: () => string | null;
  private _ratings = observable.map<string, Rating>();
  private _feedbackAnswerId: string | null = null;

  constructor({ organizationId, sessionId }: AnswerRatingStoreParams) {
    this._organizationId = String(organizationId);
    this._sessionId = sessionId;

    makeObservable<AnswerRatingStore, '_ratings' | '_feedbackAnswerId'>(this, {
      _ratings: observable,
      _feedbackAnswerId: observable,
      setRating: action.bound,
      putRate: action.bound,
      openFeedbackModal: action.bound,
      closeFeedbackModal: action.bound,
      canShowSuccessModal: computed,
      feedbackAnswerId: computed,
    });
  }

  get canShowSuccessModal(): boolean {
    return this.feedbackMeta.isLoaded && this.meta.isLoaded;
  }

  get feedbackAnswerId(): string | null {
    return this._feedbackAnswerId;
  }

  wasPositiveAnswer = (id?: string): boolean => {
    if (!id) {
      return false;
    }

    return this._ratings.get(id) === 'positive' && this.meta.isLoaded;
  };

  wasNegativeAnswer = (id?: string): boolean => {
    if (!id) {
      return false;
    }

    return this._ratings.get(id) === 'negative' && this.meta.isLoaded;
  };

  setRating(messageId: string, rating: Rating) {
    this._ratings.set(messageId, rating);
  }

  openFeedbackModal = (answerId?: string) => {
    if (!answerId) {
      return;
    }

    this._feedbackAnswerId = answerId;
    this.feedbackMeta.reset();
    this.fieldText.setValue(null, { noTouch: true });
    this.feedbackModal.open();
  };

  closeFeedbackModal = () => {
    this.feedbackModal.close();
    this._feedbackAnswerId = null;
    this.feedbackMeta.reset();
    this.fieldText.setValue(null, { noTouch: true });
  };

  private _sendFeedback = async (messageId?: string) => {
    const sessionId = this._sessionId();

    if (!sessionId || !messageId) {
      return;
    }

    this.feedbackMeta.setLoadedStartMeta();

    const response = await call({
      endpoint: ENDPOINTS.chartSessions.sendFeedback(this._organizationId, sessionId, messageId),
      method: 'POST',
      data: {
        feedback: this.fieldText.value ?? '',
      },
    });

    if (response.isError) {
      notificationsStore.showError(response.error.response?.data.message ?? 'Не удалось отправить ответ');
      this.feedbackMeta.setLoadedErrorMeta();

      return;
    }

    this.feedbackMeta.setLoadedSuccessMeta();
  };

  putNegativeRate = async (messageId?: string) => {
    const targetMessageId = messageId ?? this._feedbackAnswerId ?? undefined;

    if (!targetMessageId) {
      notificationsStore.showError('Не удалось определить сообщение для обратной связи');

      return;
    }

    await this.putRate(targetMessageId, 'negative');
  };

  async putRate(messageId?: string, rating?: Rating): Promise<void> {
    const sessionId = this._sessionId();

    if (!sessionId || !messageId) {
      return;
    }

    this.meta.setLoadedStartMeta();

    if (rating) {
      this.setRating(messageId, rating);
    }

    const current = this._ratings.get(messageId);

    if (!current) {
      return;
    }

    if (current === 'negative') {
      await this._sendFeedback(messageId);
    }

    const response = await call({
      endpoint: ENDPOINTS.chartSessions.putRate(this._organizationId, sessionId, messageId),
      method: 'POST',
      data: current === 'positive' ? { rating: 1 } : { rating: -1 },
    });

    if (response.isError) {
      notificationsStore.showError(response.error.response?.data.message ?? 'Не удалось отпрвить оценку');
      this.meta.setLoadedErrorMeta();
      this._ratings.delete(messageId);

      return;
    }

    this.meta.setLoadedSuccessMeta();
  }

  destroy() {
    this.meta.destroy();
    this.feedbackMeta.destroy();
    this.feedbackModal.destroy();
    this.successFeedbackModal.destroy();
    this.fieldText.destroy();
    this._feedbackAnswerId = null;
  }
}
